(ns app.front.view.bim.core
  (:require [app.domain.bim.attachment-model :as bim-attachment-model]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.process :as process]
            [app.front.ui.accordion :as accordion]
            [app.front.ui.table :as ui-table]
            [app.front.view.attachment.upload :as att-upload]
            [app.front.view.bim.upload :as bim-upload]
            [app.front.view.workspace.section :as ws-section]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::bim-attachments
  :<- [::entities/context]
  (fn [ctx [_ parent-id]]
    (->> (entities/find-entities ctx {:entity/parent parent-id
                                      :entity/type   :entity.type/bim-attachment})
         (sort-by :entity/created))))

(rf/reg-sub ::bim-attachment-versions
  :<- [::entities/context]
  (fn [ctx [_ attachment-id]]
    (->> (entities/find-entities ctx {:entity/parent attachment-id
                                      :entity/type   :entity.type/bim-attachment-version})
         (sort-by :bim-attachment-version/version >))))

(rf/reg-event-fx ::fetch-check-result
  (fn [_ [_ version-id check-type]]
    {:comm/command {:command    :bim/get-check-result
                    :method     :get
                    :data       {:bim-attachment-version-id version-id
                                 :type                      check-type}
                    :on-success [::fetch-check-result.success version-id check-type]}}))

(rf/reg-event-db ::fetch-check-result.success
  (fn [db [_ version-id type _ response]]
    (assoc-in db [::check-results version-id type] response)))

(rf/reg-sub ::validation-result
  (fn [db [_ version-id check-type]]
    (->> (get-in db [::check-results version-id check-type])
         :rules
         (group-by :status)
         (p/map-keys (comp keyword str/lower-case)))))

(rf/reg-sub ::rule-set-map
  (fn [db [_ version-id check-type]]
    (->> (get-in db [::check-results version-id check-type])
         :ruleSets
         (map (juxt :id :name))
         (into {}))))

(defn model-checks-table [tr rule-sets items]
  [:div.p-2
   [ui-table/modern-responsive-table
    {:tr      tr
     :items   items
     :id-func :id
     :cols    [[:bim-section/rule-name
                (fn [item]
                  [:<>
                   (some->> (:path item)
                            (keep #(get rule-sets %))
                            (str/join " > ")
                            seq
                            (into [:div.font-semibold.mb-1]))
                   [:div (:name item)]])]
               [:bim-section/rule-message
                :statusMessage
                {:max-width "15%"}]
               [:bim-section/failed-components
                :failedComponentsAmount
                {:width "180px"}]
               [:bim-section/checked-components
                :checkedComponentsAmount
                {:width "180px"}]]}]])

(defn validation-result-view [tr version-id check-type result-key]
  (r/with-let [_ (>evt [::fetch-check-result version-id check-type])]
    (let [{:keys [passed failed irrelevant]} (<sub [::validation-result version-id check-type])
          rule-sets                          (<sub [::rule-set-map version-id check-type])]
      [:div
       (if (or passed failed irrelevant)
         [:div.border.border-solid.border-ui-gray.p-2.rounded-md
          [:div.flex.items-center.justify-between.my-2
           [:div.font-semibold (tr [(keyword "bim-check-type" (name check-type))])]
           (when (seq failed)
             (cc/button {:kind :tertiary
                         :size :small
                         :icon i/download
                         :href (file-download/download-href
                                 :bim/get-bcf-file
                                 {:bim-attachment-version-id version-id
                                  :type                      (name check-type)})}
                        "BCF"))]
          [accordion/blue-header-accordion
           (str "bim-result-" version-id "-" result-key)
           tr
           {:initial-state :first-open}
           (for [[items title color] [[passed :bim-section/passed-checks :green]
                                      [failed :bim-section/failed-checks :red]
                                      [irrelevant :bim-section/irrelevant-checks :gray]]]
             {:header-pill-text  (count items)
              :header-pill-color color
              :header-text       (tr [title])
              :header-info       ""
              :content           [model-checks-table tr rule-sets items]})]]
         [:div
          (cc/inline-loading {:description (tr [:bim-section/loading-validation])})])])))

(defn version-expanded-view
  [tr phase {:db/keys [id] :bim-attachment-version/keys [validation-result-file-object-key
                                                         compliance-check-file-object-key]}]
  (when (or validation-result-file-object-key compliance-check-file-object-key)
    [:div.flex.flex-col.gap-4
     (when validation-result-file-object-key
       ^{:key (str id "-" validation-result-file-object-key)}
       [validation-result-view tr id :model-validation validation-result-file-object-key])
     (when compliance-check-file-object-key
       ^{:key (str id "-" compliance-check-file-object-key)}
       [validation-result-view tr id :code-compliance compliance-check-file-object-key])
     (let [compliance-check-signal [:process.signal/bim-attachment-version-compliance-check {:attachment-version-id id}]
           check-allowed?          (<sub [::process/signal-permitted? phase compliance-check-signal])]
       (when check-allowed?
         [:div.flex.justify-end
          (cc/button {:kind     :tertiary
                      :size     :small
                      :icon     i/file-check
                      :disabled (<sub [::process/signal-processing? :process.signal/bim-attachment-version-compliance-check])
                      :on-click #(>evt [::process/signal phase compliance-check-signal
                                        {:notify {:success :bim-section/check-code-compliance-success}}])}
                     (tr [:bim-section/check-code-compliance]))]))]))

(defn validation-state->row-status [validation-state]
  (case validation-state
    :bim-attachment-version.validation-state/valid :ok
    :bim-attachment-version.validation-state/invalid :warning
    :bim-attachment-version.validation-state/in-progress :task
    nil))

(defn status-column [tr validation-state]
  (when validation-state
    (if (contains? #{:bim-attachment-version.validation-state/in-progress
                     :bim-attachment-version.compliance-state/in-progress}
                   validation-state)
      (cc/inline-loading {:description (tr [validation-state])})
      (tr [validation-state]))))

(defn version-download-link [{:bim-attachment-version/keys [filename] :db/keys [id]}]
  (cc/link {:href       (file-download/download-href :bim/download-attachment-version
                                                     {:bim-attachment-version-id id})
            :class-name "font-semibold"}
           filename))

(defn bim-attachment-expanded-view [tr phase attachment]
  (let [versions         (<sub [::bim-attachment-versions (:db/id attachment)])
        format-date-time (<sub [::date-util/date-time-fn])]
    [:div.px-6
     [:div.font-bold.my-4 (tr [:bim-section/versions])]
     [:div.bg-ui-blue-10.p-4.rounded-md
      [ui-table/modern-responsive-table
       {:tr            tr
        :items         versions
        :cols          [[:bim-attachment-version/filename
                         version-download-link
                         {:width "33%"}]
                        [:bim-attachment-version/version
                         :bim-attachment-version/version
                         {:width "100px"}]
                        [:bim-attachment-version/size
                         (comp att-upload/format-size :bim-attachment-version/size)
                         {:width "100px"}]
                        [:bim-attachment-version/validation-state
                         #(->> % :bim-attachment-version/validation-state (status-column tr))]
                        [:bim-attachment-version/compliance-state
                         #(->> % :bim-attachment-version/compliance-state (status-column tr))]
                        [:created
                         #(-> % :entity/created format-date-time)]]
        :get-status    #(-> % :bim-attachment-version/validation-state validation-state->row-status)
        :expanded-func (partial version-expanded-view tr phase)
        :make-actions  (fn [{:db/keys [id]}]
                         [(when (<sub [::process/signal-permitted? phase
                                       [:process.signal/bim-attachment-delete-version {:attachment-version-id id}]])
                            {:title           (tr [:bim-section/delete-attachment-version])
                             :on-click        #(>evt [::process/signal
                                                      phase
                                                      [:process.signal/bim-attachment-delete-version
                                                       {:attachment-version-id id}]
                                                      {:notify {:success :bim-section/delete-attachment-version-success}}])
                             :icon            i/trash
                             :confirm?        true
                             :confirm-content (tr [:bim-section/delete-attachment-version-confirm])})])}]]
     [:div.flex.w-full.justify-end.mt-4
      [bim-upload/upload-version-button (util/mmap {:attachment-id (:db/id attachment)}
                                                   phase tr)]]]))

(defn bim-table [{:keys [tr phase]}]
  (let [attachments      (<sub [::bim-attachments (:db/id phase)])
        format-date-time (<sub [::date-util/date-time-fn])]
    [:div
     [ui-table/modern-responsive-table
      {:tr            tr
       :items         attachments
       :cols          [[:bim-attachment/type
                        (fn [{:bim-attachment/keys [type description]}]
                          [:<>
                           [:div.font-semibold (tr [type])]
                           (when-not (str/blank? description)
                             [:div.mt-1 description])])
                        (comp tr vector :bim-attachment/type)]
                       [:bim-attachment-version/filename
                        (fn [bim-attachment]
                          (when-let [version (bim-attachment-model/latest-version bim-attachment)]
                            [:<>
                             [:div
                              [version-download-link version]]
                             [:div.mt-1 (tr [:attachments/version-number]
                                            [(:bim-attachment-version/version version)])]]))]
                       [:bim-attachment-version/validation-state
                        #(->> % bim-attachment-model/validation-state (status-column tr))]
                       [:bim-attachment-version/compliance-state
                        #(->> % bim-attachment-model/compliance-state (status-column tr))]
                       [:modified
                        #(some-> % bim-attachment-model/latest-version :entity/created format-date-time)]]
       :get-status    #(-> % bim-attachment-model/validation-state validation-state->row-status)
       :expanded-func (partial bim-attachment-expanded-view tr phase)
       :make-actions  (fn [{:db/keys [id]}]
                        [(when (<sub [::process/signal-permitted? phase
                                      [:process.signal/bim-attachment-delete {:attachment-id id}]])
                           {:title           (tr [:bim-section/delete-attachment])
                            :on-click        #(>evt [::process/signal
                                                     phase
                                                     [:process.signal/bim-attachment-delete {:attachment-id id}]
                                                     {:notify {:success :bim-section/delete-attachment-success}}])
                            :icon            i/trash
                            :confirm?        true
                            :confirm-content (tr [:bim-section/delete-attachment-confirm])})])}]]))

(defn section [{:keys [phase state tr]}]
  [ws-section/section
   phase
   :bim-section
   state
   :bim-section/heading
   false
   [:div
    [bim-table (util/mmap phase tr)]
    [bim-upload/upload-area tr phase]]])
